import * as React from 'react';

import { SEO } from 'main/components';

import iconLocation from '../../../main/assets/icons/location.png';
import iconClock from '../../../main/assets/icons/clock.png';

export interface JobLocation {
  uuid: string;
  name: string;
  country: string;
  city: string;
  postalCode?: string;
  streetAddress?: string;
}

export interface JobListing {
  uuid: string;
  title: string;
  slug: string;
  shortDescription: string;
  deadline: string | null;
  visibilityState: string;
  isAcceptingApplications: boolean;
  coverPhotoUrl: string;
  publishState: string;
  position: number;
  createdByUuid: string;
  createdAt: string;
  completedAt: string | null;
  valueCustomFields: any[];
  remoteType?: 'on_site' | 'hybrid' | 'fully_remote' | null;
  locations?: JobLocation[];
  cvField?: Record<string, any>;
  nameField?: Record<string, any>;
  emailField?: Record<string, any>;
}

interface State {
  jobListings: JobListing[];
  loading: boolean;
  error: string | null;
}

const remoteTypeMap: Record<string, string> = {
  on_site: 'On-site',
  hybrid: 'Hybrid',
  fully_remote: 'Fully Remote',
};

export class Jobs extends React.Component<{}, State> {
  state: State = {
    jobListings: [],
    loading: true,
    error: null,
  };

  componentDidMount() {
    this.fetchJobListings();
  }

  fetchJobListings = async () => {
    const apiKey = process.env.REACT_APP_PONTA_API_KEY || '';
    try {
      const jobListingResponse = await fetch(
        'https://api.pontahr.com/v1/job-listings',
        {
          headers: {
            'x-api-key': apiKey,
          },
        },
      );
      if (!jobListingResponse.ok) {
        throw new Error('Failed to fetch job listings');
      }
      const jobListing = await jobListingResponse.json();

      const sortedJobs: JobListing[] = jobListing.sort(
        (a: JobListing, b: JobListing) => a.position - b.position,
      );

      this.setState({
        jobListings: sortedJobs,
        loading: false,
      });
    } catch (error) {
      this.setState({
        error: (error as any)?.message,
        loading: false,
      });
    }
  };

  renderJobListings = () => {
    const { jobListings } = this.state;

    return jobListings.map((job, index) => {
      const remoteTypeText = job.remoteType
        ? remoteTypeMap[job.remoteType]
        : null;

      const hasNewLocations = job.locations && job.locations.length > 0;

      const legacyTypeField = job.valueCustomFields?.find(
        field => field.name === 'Type' && field.data?.value,
      );
      const legacyLocationField = job.valueCustomFields?.find(
        field => field.name === 'Location' && field.data?.value,
      );

      const typeLabel = remoteTypeText || legacyTypeField?.data?.value;
      const hasChips = typeLabel || hasNewLocations || legacyLocationField;

      return (
        <React.Fragment key={job.uuid}>
          <hr className="jobs-hr" />
          <div className="job-card">
            <div className="job-details">
              <h2 className="job-title">
                <a
                  href={`https://orqafpv.pontahr.com/listings/${job.slug}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  onMouseDown={event => event.preventDefault()}
                >
                  {job.title}
                </a>
              </h2>
              <p className="job-description">{job.shortDescription}</p>

              {hasChips && (
                <div className="chips">
                  {hasNewLocations && (
                    <div className="chip-container">
                      {job.locations?.map(loc => {
                        const country =
                          loc.country === 'HR' ? 'Croatia' : loc.country;
                        const label =
                          loc.city && country
                            ? `${loc.city}, ${country}`
                            : loc.city || loc.name || loc.country;

                        return (
                          <div className="chip" key={loc.uuid || label}>
                            <img
                              src={iconLocation}
                              className="chip-icon"
                              alt="Location Icon"
                            />
                            <span>{label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {typeLabel && (
                    <div className="chip">
                      <img
                        src={iconClock}
                        className="chip-icon"
                        alt="Clock Icon"
                      />
                      <span>{typeLabel}</span>
                    </div>
                  )}

                  {!hasNewLocations && legacyLocationField && (
                    <div
                      className="chip-container"
                      key={legacyLocationField.uuid}
                    >
                      {(legacyLocationField.data.value as string)
                        .split(',')
                        .map(loc => loc.trim())
                        .map(location => (
                          <div className="chip" key={location}>
                            <img
                              src={iconLocation}
                              className="chip-icon"
                              alt="Location Icon"
                            />
                            <span>{location}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="job-action">
              <a
                href={`https://orqafpv.pontahr.com/listings/${job.slug}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="apply-link"
                onMouseDown={event => event.preventDefault()}
              >
                Apply ↗
              </a>
            </div>
          </div>
          {index === jobListings.length - 1 && <hr className="jobs-hr" />}
        </React.Fragment>
      );
    });
  };

  render() {
    const { loading, error } = this.state;

    return (
      <>
        <SEO
          seoTitle="Drone Careers - Research and Development Jobs - Orqa"
          seoDescription="We are looking for passionate employees to help us achieve our mission: to become the world's leading technology provider for First Person View (FPV) and advanced Remote Reality."
        />
        <section className="correct-page-margin" style={{ marginRight: 0 }}>
          <section className="">
            <div
              className="wrapper wrapper--narrow"
              style={{ textAlign: 'center', margin: 'auto', marginTop: '1rem' }}
            >
              <h1 className="jobs-page-title" style={{}}>
                Be Part Of Our Mission
              </h1>
              <p
                className="correct-page-description"
                style={{ textAlign: 'start', marginBottom: '64px' }}
              >
                We are looking for passionate employees to help us achieve our
                mission: to become the world's leading technology provider for
                First Person View (FPV) and advanced Remote Reality.
              </p>
            </div>
          </section>

          {loading ? (
            <div style={{}}>
              <div className="loading-spinner" />
            </div>
          ) : (
            <>
              {error && (
                <p
                  className="t-delta t-error"
                  style={{ color: '#ee2737', textAlign: 'center' }}
                >
                  Error: {error}
                </p>
              )}
              {!error && this.renderJobListings()}
            </>
          )}

          {!error && !loading && (
            <div
              className="wrapper wrapper--ultranarrow"
              style={{ marginTop: '4rem' }}
            >
              <p
                className="t-epsilon s-bottom--sml"
                style={{
                  textAlign: 'center',
                  marginTop: '99.3px',
                }}
              >
                If you wish to be a part of a dynamic and creative working
                environment, Orqa's diverse team is definitely right for you!
              </p>
            </div>
          )}
        </section>
      </>
    );
  }
}

export default Jobs;
